import { sqlQuery, TRDocumentContentLibrary } from '@/shared/helpers/api.ts';
import { http } from '@/shared/httpWrapper.js';

export type TRDocumentRecord = {
    name: string;
    identity: string;
    description: string;
    validityContext: string;
    validityContextDescription: string;
    technology: string;
    technologyDescription: string;
};

export async function getAllTRDocuments(): Promise<TRDocumentRecord[]> {
    return (await sqlQuery(http, {
        query: `
            SELECT
              d.name,
              d.[identity],
              d.description,
              d.validityContext,
              v.description AS validityContextDescription,
              d.technology,
              t.description AS technologyDescription
            FROM TRDocument d
            INNER JOIN TechnologyType t ON t.name = d.technology
            INNER JOIN TRValidityContext v ON v.name = d.validityContext
            WHERE d.IsValid = 1
            ORDER BY d.name
        `
    })).data as TRDocumentRecord[];
}

export type TRRevisionQueryTemplate = {
    id: number;
    name: string;
    trDocument: string;
    sequenceNo: number;
    trReference: string;
    queryId: string;
    linkText: string;
};

export async function getQueryTemplates(trDocument: TRDocumentRecord): Promise<TRRevisionQueryTemplate[]> {
    return (await sqlQuery(http, {
        query: `
            SELECT
              id,
              name,
              trDocument,
              sequenceNo,
              trReference,
              queryId,
              linkText
            FROM TRRevisionQueryTemplate
            WHERE IsValid = 1
              AND TRDocument = @documentIdentity
        `,
        arguments: {
            documentIdentity: trDocument.identity
        }
    })).data as TRRevisionQueryTemplate[];
}

export type TRRevisionRecord = {
    name: string;
    identity: string;
    description: string;
    trDocument: string;
    areDataInitialized: boolean;
    areDataFinished: boolean;
    areQueriesFinished: boolean;
    areURLsFinished: boolean;
    isAllFinished: boolean;
    issueDate: string;
};

export async function getAllTRRevisions(): Promise<TRRevisionRecord[]> {
    return (await sqlQuery(http, {
        query: `
            SELECT
              name,
              [identity],
              description,
              trDocument,
              areDataInitialized,
              areDataFinished,
              areQueriesFinished,
              areURLsFinished,
              isAllFinished,
              issueDate
            FROM TRRevision
            WHERE IsValid = 1
            ORDER BY DateCreated
        `
    })).data as TRRevisionRecord[];
}

/**
 * The possible actions that can be taken on a content item in a TRRevision.
 * - 'Stay': The content item remains unchanged.
 * - 'Remove': The content item is removed from the TRRevision.
 * - 'Add': The content item is added to the TRRevision.
 * - 'Update': The content item will stay as part of next revision, but 'description' and/or 'trNotes' is updated in the TRRevision.
 */
export type RevisionAction = 'Stay' | 'Remove' | 'Add' | 'Update';

export type ContentItem = {
    sourceItem: string;
    trRevision: string;
    name: string;
    trReference: string;
    description: string | null;
    trNotes: string | null;
    revisionAction: RevisionAction;
    revisionText: string | null;
    dateUpdated: string;
};

export async function getAllContentItems(
    trRevision: TRRevisionRecord,
    contentLibrary: TRDocumentContentLibrary
): Promise<ContentItem[]> {
    // Assumption: The library being referred (for instance MasterTagType) will have an
    // attributed named "CoprorateXXXXNotes" where XXXX is the name of the TRDocument.
    // This must be true for all TRDocuments/content library combination where content have been added.

    return (await sqlQuery(http, {
        query: `
            SELECT
              c.sourceItem,
              c.TRRevision as trRevision,
              x.name,
              x.[${contentLibrary.trReferenceAttributeName}] as trReference,
              c.revisionAction,
              c.revisionText,
              c.description,
              c.trNotes,
              c.dateUpdated
            FROM ${contentLibrary.name} c
            JOIN ${contentLibrary.contentForLibrary} x
              ON x.[Id] = c.[SourceItem_ID]
            WHERE c.TRRevision = @trRevisionIdentity
            AND c.IsValid = 1
            ORDER BY x.[identity]
        `, arguments: { trRevisionIdentity: trRevision.identity }
    })).data as ContentItem[];
}

export type TRRevisionAction = {
    Name: string;
    Description: string;
};

export async function getAllTrRevisionActions() {
    return (await sqlQuery(http, {
        query: `
            SELECT
              Name,
              Description
            FROM TRRevisionAction
            WHERE IsValid = 1
        `
    })).data as TRRevisionAction[];
}
